import Util from "./Util";

// 定义接口来确保 objKey 有 __s_id 属性
interface EventObject {
    __s_id?: string;

    [key: string]: any;
}

interface EventNode {
    target: any;
    prev: EventNode | null;
    next: EventNode | null;
    eventType: string | number;
    callback: Function | null;  // 允许 null 赋值
    objKey: EventObject | null;
}

interface EventList {
    head: EventNode | null;
    tail: EventNode | null;
    count: number;
    isLocked: boolean;
    isBlocked: boolean;
}

export class SEventContainer {
    private _eventCallbacks: Record<string | number, EventList> = {};
    private _objectEventMap: Record<string, EventNode[]> = {};  // 这里改成字符串索引
    private _delayedEvents: any[] = [];

    constructor() {

    }

    private static addNode(list: EventList, node: EventNode): EventNode {
        if (!list.head) {
            list.head = node;
            list.tail = node;
            list.count = 1;
        } else {
            node.prev = list.tail;
            if (list.tail) {
                list.tail.next = node;
            }
            list.tail = node;
            list.count++;
        }
        return node;
    }

    private static removeNode(list: EventList, node: EventNode): void {
        const {prev, next} = node;

        if (prev) {
            prev.next = next;
        }
        if (next) {
            next.prev = prev;
        }
        if (list.head === node) {
            list.head = next;
        }
        if (list.tail === node) {
            list.tail = prev;
        }
        list.count--;
    }

    on(objKey: EventObject, eventType: string | number, callback: Function, target?: any): void {
        let eventList = this._eventCallbacks[eventType];
        if (!eventList) {
            eventList = {head: null, tail: null, count: 0, isLocked: false, isBlocked: false};
            this._eventCallbacks[eventType] = eventList;
        }

        target = target ?? objKey;
        const node: EventNode = {
            target,
            prev: null,
            next: null,
            eventType,
            callback,
            objKey,
        };

        SEventContainer.addNode(eventList, node);

        const objId = Util.checkObjId(objKey);
        let eventNodes = this._objectEventMap[objId];
        if (!eventNodes) {
            eventNodes = [];
            this._objectEventMap[objId] = eventNodes;
        }

        eventNodes.push(node);
    }

    off(objKey: EventObject): void {
        const objId = objKey.__s_id;
        if (objId == null) return;

        const eventNodes = this._objectEventMap[objId];
        if (eventNodes) {
            const eventCallbacks = this._eventCallbacks;
            for (const node of eventNodes) {
                SEventContainer.removeNode(eventCallbacks[node.eventType], node);
                node.target = null;
                node.callback = null;
                node.objKey = null;
            }
            this._objectEventMap[objId] = [];
        }
    }

    offEvent(objKey: EventObject, eventType: string | number): void {
        const objId = objKey.__s_id;
        if (objId == null) return;

        const eventNodes = this._objectEventMap[objId];
        if (eventNodes) {
            const eventCallbacks = this._eventCallbacks;
            for (const node of eventNodes) {
                if (node.eventType === eventType) {
                    SEventContainer.removeNode(eventCallbacks[node.eventType], node);
                    node.target = null;
                    node.callback = null;
                    node.objKey = null;
                }
            }
        }
    }

    invoke(eventType: string | number, args?: any[]): void {
        const eventList = this._eventCallbacks[eventType];
        if (!eventList || eventList.isBlocked) return;

        if (eventList.isLocked) {
            if (!this._delayedEvents) {
                this._delayedEvents = [];
            }
            this._delayedEvents.push(eventType, args);
        } else {
            eventList.isLocked = true;
            let node = eventList.head;
            while (node) {
                if (node.callback) {
                    node.callback.apply(node.target, args ?? []);
                }
                node = node.next;
            }
            eventList.isLocked = false;
            this._processDelayedEvents();
        }
    }

    private _processDelayedEvents(): void {
        if (this._delayedEvents.length > 0) {
            for (let i = 0; i < this._delayedEvents.length; i += 2) {
                const eventType = this._delayedEvents[i];
                const args = this._delayedEvents[i + 1];
                const eventList = this._eventCallbacks[eventType];
                if (eventList && !eventList.isLocked) {
                    eventList.isBlocked = true;
                    let node = eventList.head;
                    while (node) {
                        if (node.callback) {
                            node.callback.apply(node.target, args ?? []);
                        }
                        node = node.next;
                    }
                    eventList.isBlocked = false;
                }
            }
            this._delayedEvents.length = 0;
        }
    }
}
